<script setup>
import AppLayout from '@/Layouts/AppLayout.vue';
import { computed } from "vue";
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { Chart, registerables } from 'chart.js';
import { BarChart, useBarChart } from 'vue-chart-3';
import { Link } from '@inertiajs/vue3';

const props = defineProps({
    year: String,
    agent: Object,
    flash: Object,
    user: Object,
    agentBookingsConfirmed: Array,
    agentBookingsPending: Array,
    agentBookingsCancelled: Array,
});
Chart.register(...registerables);
dayjs.extend(advancedFormat);

const chartData = computed(() => {
    return {
        labels: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
        datasets: [
            {
                label: 'Confirmed Bookings',
                data: props.agentBookingsConfirmed,
                backgroundColor: '#c5e1a5',
                borderColor: '#c5e1a5',
                borderWidth: 1,
            },
            {
                label: 'Cancelled Bookings',
                data: props.agentBookingsCancelled,
                backgroundColor: '#ef9a9a',
                borderColor: '#ef9a9a',
                borderWidth: 1,
            },
            {
                label: 'Pending Bookings',
                data: props.agentBookingsPending,
                backgroundColor: '#ffcc80',
                borderColor: '#ffcc80',
                borderWidth: 1,
            },
        ],
    };
});

const { barChartProps } = useBarChart({
    chartData,
});

</script>
<style scoped>
@import "../../../../public/ui/vendor/libs/toastr/toastr.css";
@import "vue-loading-overlay/dist/vue-loading.css";
</style>
<template>
    <AppLayout title="Agent Details" :year=year :user=user>
        <div class="container-xxl flex-grow-1 container-p-y">
            <div class="row">
                <div class="col-12">
                    <div class="card mb-4">
                        <div class="user-profile-header-banner">
                            <img src="/ui/img/pages/photo-1585130401366-fe05a8d813c4.avif" alt="Banner image"
                                class="rounded-top" />
                        </div>
                        <div class="user-profile-header d-flex flex-column flex-sm-row text-sm-start text-center mb-4">
                            <div class="flex-shrink-0 mt-n2 mx-sm-0 mx-auto">
                                <img :src="'/fetchAgentLogo/d?id=' + agent.id + '&type=logo&w=200&h=200&fit=crop'"
                                    :alt="agent.agency_name" class="d-block h-auto ms-0 ms-sm-4 rounded user-profile-img" />
                            </div>
                            <div class="flex-grow-1 mt-3 mt-sm-5">
                                <div
                                    class="d-flex align-items-md-end align-items-sm-start align-items-center justify-content-md-between justify-content-start mx-4 flex-md-row flex-column gap-4">
                                    <div class="user-profile-info">
                                        <h4>{{ agent.agency_name }}</h4>
                                        <ul
                                            class="list-inline mb-0 d-flex align-items-center flex-wrap justify-content-sm-start justify-content-center gap-2">

                                            <li class="list-inline-item fw-semibold"><i class="bx bx-map"></i>
                                                {{
                                                    agent?.city?.name
                                                }}, {{ agent?.country?.name }}</li>
                                            <li class="list-inline-item fw-semibold">
                                                <i class="bx bx-calendar-alt"></i> Joined {{
                                                    dayjs(agent.created_at).format('Do MMMM YYYY')
                                                }}
                                        </li>
                                            <li>
                                                <span v-if="agent.active == 1" class="badge bg-label-success">
                                                    <i class="bx bxs-bookmark"></i> Active
                                                </span>
                                                <span v-else class="badge bg-label-danger">
                                                    <i class="bx bxs-bookmark"></i> Inactive
                                                </span>
                                            </li>
                                        </ul>
                                    </div>
                                    <div class="btn-group" id="dropdown-icon-demo">
                                        <button type="button" class="btn btn-primary dropdown-toggle"
                                            data-bs-toggle="dropdown" aria-expanded="false">
                                            <i class="bx bx-menu bx-tada"></i> Actions
                                        </button>
                                        <ul class="dropdown-menu">
                                            <li>
                                                <Link :href="'/agents/edit/' + agent.id"
                                                    v-if="$page.props.user.permissions.includes('edit-agents')"
                                                    class="dropdown-item d-flex align-items-center"><i
                                                    class="bx bx-chevron-right scaleX-n1-rtl"></i>Edit Agency
                                                </Link>
                                            </li>
                                            <hr>
                                            <li v-if="agent.active == 1">
                                                <Link class="dropdown-item d-flex align-items-center text-danger"
                                                    v-if="$page.props.user.permissions.includes('disable-agency')"
                                                    data-bs-toggle="modal" href="#" data-bs-target="#cancelBookingModal"><i
                                                    class="bx bx-chevron-right scaleX-n1-rtl"></i>Disable Agency
                                                </Link>
                                            </li>
                                            <li v-else>
                                                <Link class="dropdown-item d-flex align-items-center text-success"
                                                    v-if="$page.props.user.permissions.includes('enable-agency')">
                                                <i class="bx bx-chevron-right scaleX-n1-rtl" href="#"></i>Enable Agency
                                                </Link>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!--/ Header -->

            <!-- Navbar pills -->
            <div class="row">
                <div class="col-md-12">
                    <ul class="nav nav-pills flex-column flex-sm-row mb-4">
                        <li class="nav-item">
                            <a class="nav-link active" href="javascript:void(0);"><i class="bx bx-user"></i> Profile</a>
                        </li>
                        <!-- <li class="nav-item">
                                        <a class="nav-link" href="pages-profile-teams.html"><i class="bx bx-group"></i> Users</a>
                                    </li> -->
                    </ul>
                </div>
            </div>
            <!--/ Navbar pills -->

            <!-- User Profile Content -->
            <div class="row">
                <div class="col-xl-4 col-lg-5 col-md-5">
                    <!-- About User -->
                    <div class="card mb-4">
                        <div class="card-body">
                            <small class="text-muted text-uppercase">About</small>
                            <ul class="list-unstyled mb-4 mt-3">
                                <li class="d-flex align-items-center mb-3">
                                    <i class="bx bx-user"></i><span class="fw-semibold mx-2">Name:</span>
                                    <span>{{ agent.agency_name }}</span>
                                </li>
                                <li class="d-flex align-items-center mb-3">
                                    <i class="bx bx-globe"></i><span class="fw-semibold mx-2">Country:</span>
                                    <span>{{ agent.country?.name }}</span>
                                </li>
                                <li class="d-flex align-items-center mb-3">
                                    <i class="bx bx-map"></i><span class="fw-semibold mx-2">City:</span>
                                    <span>{{ agent.city?.name }}</span>
                                </li>
                                <li class="d-flex align-items-center mb-3">
                                    <i class="bx bx-detail"></i><span class="fw-semibold mx-2">TIN:</span>
                                    <span>{{ agent.agency_TIN }}</span>
                                </li>
                                <li class="d-flex align-items-center mb-3">
                                    <i class="bx bx-detail"></i><span class="fw-semibold mx-2">Pan No:</span>
                                    <span>{{ agent.pan_number }}</span>
                                </li>
                                <li class="d-flex align-items-center mb-3">
                                    <i class="bx bx-detail"></i><span class="fw-semibold mx-2">Address:</span>
                                    <span>{{ agent.agency_address }}</span>
                                </li>
                            </ul>
                            <small class="text-muted text-uppercase">Contacts</small>
                            <ul class="list-unstyled mb-4 mt-3">
                                <li class="d-flex align-items-center mb-3">
                                    <i class="bx bx-phone"></i><span class="fw-semibold mx-2">Telephone:</span>
                                    <span>{{ agent.agency_telephone }}</span>
                                </li>
                                <li class="d-flex align-items-center mb-3">
                                    <i class="bx bx-mobile"></i><span class="fw-semibold mx-2">Mobile:</span>
                                    <span>{{ agent.agency_mobile }}</span>
                                </li>
                                <li class="d-flex align-items-center mb-3">
                                    <i class="bx bx-envelope"></i><span class="fw-semibold mx-2">Email:</span>
                                    <span>{{ agent.agency_email }}</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div class="col-xl-8 col-lg-7 col-md-7">
                    <div class="card card-action mb-4">
                        <div class="card-header align-items-center">
                            <h5 class="card-action-title mb-0"><i class="bx bx-chart me-2"></i>Bookings
                                Chart {{ year }}</h5>
                        </div>
                        <div class="card-body">
                            <BarChart v-bind="barChartProps" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </AppLayout>
</template>